import React, { createContext, useContext, useMemo, useEffect, type ReactNode } from 'react'
import { AgentBuilderClient, type AgentBuilderConfig } from '@standardagents/client'
import { __setGlobalEndpoint } from '../services/sendMessage'
import { __setGlobalEndpointForStop } from '../services/stopThread'

interface AgentBuilderContextValue {
  client: AgentBuilderClient
  config: AgentBuilderConfig
}

export const AgentBuilderContext = createContext<AgentBuilderContextValue | null>(null)

export interface AgentBuilderProviderProps {
  config: AgentBuilderConfig
  children: ReactNode
}

/**
 * AgentBuilderProvider provides the AgentBuilder client instance to all child components.
 * This should wrap the part of your app where you want to use AgentBuilder functionality.
 *
 * @example
 * ```tsx
 * <AgentBuilderProvider config={{ endpoint: 'https://api.example.com' }}>
 *   <YourApp />
 * </AgentBuilderProvider>
 * ```
 */
export function AgentBuilderProvider({ config, children }: AgentBuilderProviderProps) {
  // Create client instance only once using useMemo
  const client = useMemo(() => {
    return new AgentBuilderClient(config.endpoint)
  }, [config.endpoint])

  const value = useMemo(
    () => ({
      client,
      config,
    }),
    [client, config]
  )

  // Set global endpoint for standalone functions like sendMessage and stopThread
  useEffect(() => {
    __setGlobalEndpoint(config.endpoint)
    __setGlobalEndpointForStop(config.endpoint)
  }, [config.endpoint])

  return <AgentBuilderContext.Provider value={value}>{children}</AgentBuilderContext.Provider>
}

/**
 * Hook to access the AgentBuilder client instance.
 * Must be used within an AgentBuilderProvider.
 *
 * @throws Error if used outside of AgentBuilderProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const client = useAgentBuilderClient()
 *   // Use client.getThread(), client.sendMessage(), etc.
 * }
 * ```
 */
export function useAgentBuilderClient(): AgentBuilderClient {
  const context = useContext(AgentBuilderContext)

  if (!context) {
    throw new Error('useAgentBuilderClient must be used within AgentBuilderProvider')
  }

  return context.client
}

/**
 * Hook to access the AgentBuilder config.
 * Must be used within an AgentBuilderProvider.
 *
 * @throws Error if used outside of AgentBuilderProvider
 */
export function useAgentBuilderConfig(): AgentBuilderConfig {
  const context = useContext(AgentBuilderContext)

  if (!context) {
    throw new Error('useAgentBuilderConfig must be used within AgentBuilderProvider')
  }

  return context.config
}
